/**
 * Design Gallery / Template Showcase: `type_*` and `style_*` tags on templates.
 * Flipbooks and other categories use the same tagging scheme (e.g. type_flipbooks).
 */

export const TYPE_PREFIX = 'type_';
export const STYLE_PREFIX = 'style_';
export const BRAND_PREFIX = 'brand_';
export const CREATIVE_STUDIO_TAG = 'creative_studio';

function asTagArray(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
  return String(tags)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function normalizeTagKey(key) {
  return String(key || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
}

export function hasCreativeStudioTag(tags) {
  const arr = asTagArray(tags);
  return arr.some((t) => normalizeTagKey(t) === CREATIVE_STUDIO_TAG);
}

function stripPrefix(tag, prefix) {
  if (!tag || !tag.startsWith(prefix)) return null;
  const suffix = tag.slice(prefix.length);
  return suffix ? normalizeTagKey(suffix) : null;
}

const ACRONYMS = { pdp: 'PDP' };

function humanizeKey(key) {
  const k = normalizeTagKey(key);
  if (!k) return '';
  return k
    .split('_')
    .filter(Boolean)
    .map((w) => ACRONYMS[w] || (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

export function typeKeyToLabel(typeKey) {
  return humanizeKey(typeKey);
}

export function styleKeyToLabel(styleKey) {
  return humanizeKey(styleKey);
}

export function brandKeyToLabel(brandKey) {
  return humanizeKey(brandKey);
}

export function getTypeKeys(tags) {
  const arr = asTagArray(tags);
  const keys = [];
  for (const t of arr) {
    const key = stripPrefix(t, TYPE_PREFIX);
    if (key) keys.push(key);
  }
  return Array.from(new Set(keys));
}

export function getPrimaryTypeKey(tags) {
  return getTypeKeys(tags)[0] || null;
}

export function getStyleKeys(tags) {
  const arr = asTagArray(tags);
  const keys = [];
  for (const t of arr) {
    const key = stripPrefix(t, STYLE_PREFIX);
    if (key) keys.push(key);
  }
  return Array.from(new Set(keys));
}

export function getBrandKeys(tags) {
  const arr = asTagArray(tags);
  const keys = [];
  for (const t of arr) {
    const key = stripPrefix(t, BRAND_PREFIX);
    if (key) keys.push(key);
  }
  return Array.from(new Set(keys));
}

export function sortKeysByLabel(keys, keyToLabelFn) {
  const toLabel = typeof keyToLabelFn === 'function' ? keyToLabelFn : (x) => String(x || '');
  return [...keys].sort((a, b) => toLabel(a).localeCompare(toLabel(b)));
}

/**
 * Resolve `?type=` query value to a type key, or null.
 * Note: no pre-defined types; we just normalize.
 */
export function resolveTypeFilterFromQuery(query) {
  const key = normalizeTagKey(query);
  return key || null;
}

/**
 * Per-PDP-image-type subtag prefix. Templates marked with `type_pdp` may
 * additionally carry one or more `pdp_subtype_<key>` tags (e.g.
 * `pdp_subtype_hero`, `pdp_subtype_lifestyle`, `pdp_subtype_how_to`) that
 * indicate which row of the PDP image set widget the template belongs to.
 *
 * If a PDP template carries no `pdp_subtype_*` tag, it appears in no row's
 * gallery thumbs (only the global "Browse full gallery" link surfaces it).
 */
export const PDP_SUBTYPE_PREFIX = 'pdp_subtype_';

export function getPdpSubtypeKeys(tags) {
  const arr = asTagArray(tags);
  const keys = [];
  for (const t of arr) {
    const key = stripPrefix(t, PDP_SUBTYPE_PREFIX);
    if (key) keys.push(key);
  }
  return Array.from(new Set(keys));
}

/**
 * Group an array of templates by their `pdp_subtype_*` tags. A template that
 * carries multiple subtype tags appears in every matching bucket.
 *
 * Returns: { hero: [t1, t2], lifestyle: [t3], how_to: [t4, t5], ... }
 */
export function bucketTemplatesBySubtype(templates) {
  const buckets = {};
  for (const t of templates || []) {
    const subtypes = getPdpSubtypeKeys(t.tags);
    for (const key of subtypes) {
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(t);
    }
  }
  return buckets;
}
